import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
  getDashboardPaperSx,
  getDashboardSectionContentPaperSx,
} from './dashboardSurfaceSx';
import { getBookingOpsDateFieldSx } from './bookingOperationsMobileLayout';

export const STOCK_ACCENT = DASHBOARD_ACCENT;

export { getDashboardDarkSurfaceTokens, getDashboardPageShellSx };

export function isStockPdfExport() {
  return typeof document !== 'undefined' && document.body?.classList?.contains('dashboard-pdf-exporting');
}

export function getStockThemeColors(isDark) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return {
    booked: '#ff6b7d',
    directBooked: STOCK_ACCENT,
    onlineBooked: '#f8e082',
    surface: tokens.surface,
    surfaceGradient: tokens.surfaceGradient,
    surfaceBorder: tokens.surfaceBorder,
    headerBg: tokens.headerBg,
    text: tokens.text,
    subtleText: tokens.subtleText,
    success: '#66d4a8',
    danger: '#ef5350',
    info: '#8ac9ff',
    warning: '#ffb74d',
    purple: '#ba68c8',
    cyan: '#4dd0e1',
  };
}

export function getStockTabsPaperSx(isDark) {
  return {
    mb: 3,
    overflow: 'hidden',
    p: { xs: 1, sm: 1.5 },
    ...getDashboardSectionContentPaperSx(isDark, false, { p: { xs: 1, sm: 1.5 }, minHeight: 0 }),
  };
}

export function getStockDateFieldSx(isDark) {
  return getBookingOpsDateFieldSx(isDark, STOCK_ACCENT);
}

export function getStockSummaryCardSx(isDark, themeColors) {
  return {
    p: { xs: 2.2, md: 2.6 },
    background: isDark
      ? themeColors.surfaceGradient
      : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
    border: `1px solid ${alpha(themeColors.surfaceBorder, isDark ? 0.95 : 0.9)}`,
    borderRadius: 3,
    minHeight: { xs: 260, md: 280 },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 0.8,
    boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.34)' : '0 10px 26px rgba(15,23,42,0.08)',
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
  };
}

export function getStockKpiCardSx(isDark, themeColors) {
  return {
    p: 2,
    minHeight: 96,
    background: isDark
      ? themeColors.surfaceGradient
      : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
    border: `1px solid ${alpha(themeColors.surfaceBorder, isDark ? 0.95 : 0.9)}`,
    borderRadius: 3,
    boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.34)' : '0 10px 26px rgba(15,23,42,0.08)',
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
  };
}

export function getStockChartCardSx(isDark, themeColors, accentColor) {
  const accent = accentColor || themeColors.info;
  const pdfExport = isStockPdfExport();
  return {
    p: { xs: 2.2, md: 2.5 },
    background: isDark
      ? (
        pdfExport
          ? `linear-gradient(180deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
          : `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 34%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      )
      : `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, rgba(255,255,255,0.99) 28%, rgba(248,250,252,0.99) 100%)`,
    border: `1px solid ${alpha(accent, isDark ? 0.24 : 0.18)}`,
    borderRadius: 3,
    boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.34)' : '0 10px 26px rgba(15,23,42,0.08)',
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${accent} 0%, ${alpha(accent, 0.18)} 100%)`,
      zIndex: 1,
      pointerEvents: 'none',
    },
  };
}

/** Default Recharts margins — bottom reserves space so legends are not clipped. */
export function getStockChartMargin(options = {}) {
  const { withLegend = false, left = 6, right = 16, top = 8, bottom } = options;
  return {
    top,
    right,
    left,
    bottom: bottom ?? (withLegend ? 48 : 8),
  };
}

export function getStockLegendProps(themeColors, overrides = {}) {
  return {
    verticalAlign: 'bottom',
    iconType: 'circle',
    wrapperStyle: {
      fontSize: 12,
      color: themeColors.text,
      lineHeight: 1.4,
      paddingTop: 4,
    },
    ...overrides,
  };
}

/** Fixed-height plot shell — keeps Recharts SVGs from bleeding into sibling cards. */
export function getStockChartPlotSx(isDark, themeColors, height = 280) {
  return {
    position: 'relative',
    zIndex: 0,
    isolation: 'isolate',
    overflow: 'hidden',
    height,
    minHeight: height,
    width: '100%',
    mt: 0.5,
    '& .recharts-responsive-container': {
      position: 'relative !important',
    },
    '& .recharts-wrapper': {
      position: 'absolute !important',
      inset: '0 !important',
    },
    '& .recharts-legend-item-text': {
      color: `${themeColors.text} !important`,
    },
  };
}

export function getStockDashboardSectionSx() {
  return {
    position: 'relative',
    isolation: 'isolate',
    zIndex: 0,
    mb: 3,
    width: '100%',
  };
}

export function getStockInnerPanelSx(isDark, themeColors) {
  const pdfExport = isStockPdfExport();
  return {
    p: 2,
    border: `1px solid ${themeColors.surfaceBorder}`,
    borderRadius: 2,
    background: isDark
      ? (pdfExport ? THEME_COLORS.SURFACE_ELEVATED_DARK : themeColors.surfaceGradient)
      : themeColors.surface,
    height: '100%',
    boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.34)' : '0 10px 26px rgba(15,23,42,0.08)',
  };
}

export function getStockSectionPaperSx(isDark) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return {
    background: tokens.surfaceGradient,
    border: `1px solid ${tokens.surfaceBorder}`,
    borderRadius: 2,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
  };
}

/** Datatable card shell — matches Booking List / PageLayout card surface. */
export function getStockListPaperSx(isDark) {
  const themeMode = isDark ? 'dark' : 'light';
  return {
    ...getDashboardPaperSx(themeMode, 'controls'),
    mb: 0,
    p: { xs: 1.5, sm: 2, md: 2.5 },
    overflow: 'hidden',
  };
}

export function getStockListHeaderTitleSx(isDark) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return {
    fontWeight: 600,
    color: tokens.text,
    mb: 0.5,
  };
}

export function getStockListHeaderSubtitleSx(isDark) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return {
    color: tokens.subtleText,
  };
}

export function getStockDrawerHeaderSx(isDark) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return {
    borderBottom: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}

export function getStockDrawerFooterSx(isDark) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return {
    borderTop: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}
